/**
 * @file Campaigns management page.
 * @description Provides campaign listing, stats, CRUD modal flow, and status toggling.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Megaphone, 
  Plus, 
  Edit3, 
  Pause, 
  Play, 
  Trash2, 
  Users, 
  Calendar,
  Search,
  X,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { 
  CampaignsService, 
  Campaign, 
  CampaignFormData, 
  CampaignStats,
} from '../../services/campaignsService';
import { ApiService } from '../../services/apiService';
import { AdvertisersService } from '../../services/advertisersService';
import { PageHeader, Pagination } from '../../components/ui';
import { cn } from '../../utils/cn';

const PER_PAGE = 10;
const MODAL_TRANSITION_MS = 250;

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [advertisers, setAdvertisers] = useState<{ id: number; name: string }[]>([]);
  const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PER_PAGE);
  const initialLoadDoneRef = useRef(false);
  const searchDebounceRef = useRef<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<CampaignStats>({
    totalCampaigns: 0,
    activeCampaigns: 0,
    pausedCampaigns: 0,
    totalLeads: 0,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalAnimatedIn, setModalAnimatedIn] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    advertiser_id: '',
    source: '',
    description: '',
  });
  
  const [notification, setNotification] = useState<{message: string, type: 'error' | 'success'} | null>(null);


  const fetchStatsSummary = useCallback(async () => {
    try {
      const all = await CampaignsService.getCampaigns();
      setStats({
        totalCampaigns: totalRecords || all.length,
        activeCampaigns: all.filter((c) => c.status === '1').length,
        pausedCampaigns: all.filter((c) => c.status === '0').length,
        totalLeads: all.reduce((sum, c) => sum + (c.total_leads ?? 0), 0),
      });
    } catch {
      // Supplementary; table error handles main failures.
    }
  }, [totalRecords]);

  const fetchCampaigns = useCallback(async (page: number = 1) => {
    const isFirstLoad = !initialLoadDoneRef.current;
    if (!isFirstLoad) {
      setTableRefreshing(true);
    }

    try {
      const { campaigns: rows, pagination } = await CampaignsService.listCampaigns(
        page,
        PER_PAGE,
      );
      setCampaigns(rows);
      setCurrentPage(pagination.page);
      setTotalPages(Math.max(1, pagination.total_page));
      setTotalRecords(pagination.total);
      setPageSize(pagination.per_page || PER_PAGE);
      setStats((prev) => ({
        ...prev,
        totalCampaigns: pagination.total,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred');
      setCampaigns([]);
    } finally {
      setTableRefreshing(false);
      initialLoadDoneRef.current = true;
    }
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages || page === currentPage || tableRefreshing) {
        return;
      }
      void fetchCampaigns(page);
    },
    [fetchCampaigns, totalPages, currentPage, tableRefreshing],
  );

  const applySearch = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) {
        await fetchCampaigns(1);
        return;
      }

      setTableRefreshing(true);
      setError(null);
      try {
        const all = await CampaignsService.getCampaigns();
        const lower = q.toLowerCase();
        const filtered = all.filter(
          (c) =>
            c.name.toLowerCase().includes(lower) ||
            (c.description ?? '').toLowerCase().includes(lower) ||
            (c.advertiser_name ?? '').toLowerCase().includes(lower) ||
            (c.source_name ?? c.source ?? '').toLowerCase().includes(lower),
        );
        setCampaigns(filtered);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalRecords(filtered.length);
        setPageSize(Math.max(filtered.length, 1));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'An unknown error occurred while searching.');
        setCampaigns([]);
      } finally {
        setTableRefreshing(false);
      }
    },
    [fetchCampaigns],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = window.setTimeout(() => {
        void applySearch(value);
      }, 300);
    },
    [applySearch],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    void applySearch('');
  }, [applySearch]);

  const refreshCampaignTable = useCallback(async () => {
    if (searchQuery.trim()) {
      await applySearch(searchQuery);
    } else {
      await fetchCampaigns(currentPage);
    }
  }, [searchQuery, applySearch, fetchCampaigns, currentPage]);

  const fetchAdvertisers = useCallback(async () => {
    try {
      const [advertisersData, sourcesData] = await Promise.all([
        AdvertisersService.getAdvertisers().then((rows) =>
          rows.map((a) => ({ id: a.id, name: a.name })),
        ),
        ApiService.getSources(),
      ]);
      

      setAdvertisers(advertisersData);
      // Convert sources from DropdownItem[] to { id: string; name: string }[]
      setSources(sourcesData.map(source => ({
        id: source.id.toString(),
        name: source.name,
      })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchCampaigns(1), fetchAdvertisers()]);
      setIsLoading(false);
    };
    void loadData();
  }, [fetchCampaigns, fetchAdvertisers]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (totalRecords > 0) {
      void fetchStatsSummary();
    }
  }, [totalRecords, fetchStatsSummary]);

  const showNotification = (message: string, type: 'success' | 'error' = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const resetForm = () => {
    setFormData({ name: '', advertiser_id: '', source: '', description: '' });
    setFormErrors([]);
  };

  useEffect(() => {
    if (!showCreateModal) {
      setModalAnimatedIn(false);
      return;
    }
    setModalAnimatedIn(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setModalAnimatedIn(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [showCreateModal]);

  const handleOpenModal = (campaign: Campaign | null = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        advertiser_id: campaign.advertiser_id.toString(),
        source: campaign.source,
        description: campaign.description,
      });
    } else {
      setEditingCampaign(null);
      resetForm();
    }
    setFormErrors([]);
    setModalError(null);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setModalAnimatedIn(false);
    window.setTimeout(() => {
      setShowCreateModal(false);
      setEditingCampaign(null);
      resetForm();
      setModalError(null);
    }, MODAL_TRANSITION_MS);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if form data is empty (description is NOT mandatory)
    if (!formData.name || !formData.advertiser_id || !formData.source) {
      setFormErrors(['Please fill in all required fields']);
      return;
    }
    
    // Validate form data with available sources
    const validation = CampaignsService.validateCampaignForm(formData, sources);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setFormErrors([]);

    try {
      if (editingCampaign) {
        await CampaignsService.updateCampaign(editingCampaign.id, formData);
      } else {
        await CampaignsService.createCampaign(formData);
      }
      
      showNotification('Campaign saved successfully', 'success');
      handleCloseModal();
      await refreshCampaignTable();
      await fetchStatsSummary();
    } catch (error) {
      // Check if error has the API validation structure
      if (error && typeof error === 'object' && 'errors' in error && error.errors) {
        
        // Convert API errors object to array of error messages
        const validationErrors = Object.entries(error.errors).map(([field, message]) => {
          // Map field names to user-friendly names
          const fieldMap: Record<string, string> = {
            'advertiser_id': 'Advertiser',
            'name': 'Campaign name',
            'source': 'Source',
            'description': 'Description',
          };
          return `${fieldMap[field] || field}: ${message}`;
        });
        
        setFormErrors(validationErrors);
        setModalError('Please fix the validation errors below');
        return;
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a validation error from API (string format)
      if (errorMessage.includes('Validation failed:')) {
        // Extract field-specific errors and add them to form errors
        const validationErrors = errorMessage.replace('Validation failed: ', '').split(', ');
        setFormErrors(validationErrors);
        setModalError('Please fix the validation errors below');
      } else {
        // Check if it's a token expiration error
        if (errorMessage.includes('Session expired')) {
          setModalError('Your session has expired. You will be redirected to login.');
        } else {
          setModalError(errorMessage);
          showNotification(errorMessage, 'error');
        }
      }
    }
  };

  const toggleCampaignStatus = async (campaign: Campaign) => {
    const newStatus = campaign.status === '1' ? '0' : '1';
    try {
      await CampaignsService.toggleCampaignStatus(campaign.id, newStatus);
      showNotification('Status updated successfully', 'success');
      await refreshCampaignTable();
      await fetchStatsSummary();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('Session expired')) {
        showNotification('Your session has expired. You will be redirected to login.', 'error');
      } else {
        showNotification(errorMessage, 'error');
      }
    }
  };

  // Delete campaign
  const deleteCampaign = async (id: number) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this campaign. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await CampaignsService.deleteCampaign(id);
      await Swal.fire({
        title: 'Deleted!',
        text: 'Campaign has been deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      await refreshCampaignTable();
      await fetchStatsSummary();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('Session expired')) {
        await Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. You will be redirected to login.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        await Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  // Loading component
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Campaigns"
          subtitle="Manage your marketing campaigns and track performance"
          subtitleClassName="text-blue-100 text-sm"
          className='flex '
          actions={
            <div className="px-6 py-3 bg-blue-500 rounded-lg animate-pulse">
              <div className="h-5 w-32"></div>
            </div>
          }
        />

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gray-100 animate-pulse">
                  <div className="h-6 w-6"></div>
                </div>
                <div className="ml-4">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center space-x-2 text-gray-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-lg font-medium">Loading campaigns...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Campaigns"
          subtitle="Manage your marketing campaigns and track performance"
          subtitleClassName="text-blue-100 text-sm"
        />

        {/* Error Card */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Campaigns</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => {
                setIsLoading(true);
                void fetchCampaigns(1);
                void fetchAdvertisers();
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <div className={`p-4 rounded-md text-white ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {notification.message}
        </div>
      )}
      <PageHeader
        title="Campaigns"
        subtitle="Manage your marketing campaigns and track performance"
        subtitleClassName="text-blue-100 text-sm"
        actions={
          <button
            onClick={() => handleOpenModal()}
            className="px-6 py-3 bg-white text-gray-900 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors flex items-center"
          >
            <Plus size={20} className="mr-2" /> New Campaign
          </button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-center sm:justify-start flex-col sm:flex-row">
            <div className="p-3 rounded-full bg-blue-100">
              <Megaphone size={24} className="text-blue-600" />
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-4 text-center sm:text-left">
              <p className="text-2xl font-bold text-gray-900">{stats.totalCampaigns}</p>
              <p className="text-sm text-gray-600">Total Campaigns</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-center sm:justify-start flex-col sm:flex-row">
            <div className="p-3 rounded-full bg-green-100">
              <Play size={24} className="text-green-600" />
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-4 text-center sm:text-left">
              <p className="text-2xl font-bold text-gray-900">{stats.activeCampaigns}</p>
              <p className="text-sm text-gray-600">Active Campsaigns</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-center sm:justify-start flex-col sm:flex-row">
            <div className="p-3 rounded-full bg-yellow-100">
              <Pause size={24} className="text-yellow-600" />
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-4 text-center sm:text-left">
              <p className="text-2xl font-bold text-gray-900">{stats.pausedCampaigns}</p>
              <p className="text-sm text-gray-600">Paused Campaigns</p>
            </div>
          </div>
        </div>
{/* 
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-center sm:justify-start flex-col sm:flex-row">
            <div className="p-3 rounded-full bg-purple-100">
              <Users size={24} className="text-purple-600" />
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-4 text-center sm:text-left">
              <p className="text-2xl font-bold text-gray-900">{stats.totalLeads}</p>
              <p className="text-sm text-gray-600">Total Records</p>
            </div>
          </div>
        </div> */}
      </div>

      <div className="relative bg-white rounded-lg overflow-hidden border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <h3 className="shrink-0 text-lg font-semibold text-gray-900">All Campaigns</h3>
          <div className="relative w-full sm:max-w-xs sm:min-w-[16rem]">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search campaign, advertiser, source…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="Search campaigns"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {tableRefreshing && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]"
            aria-busy="true"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          </div>
        )}

        <div className="overflow-x-auto bg-white">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Advertiser</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Source</th>
                 <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Status</th> 
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Leads</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.advertiser_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">{campaign.source_name || campaign.source}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${campaign.status === '1' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{campaign.status === '1' ? 'Active' : 'Paused'}</span>
                  </td> 
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Users size={16} className="text-gray-400 mr-1" />{(campaign.total_leads || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar size={16} className="text-gray-400 mr-1" />{formatDate(campaign.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => handleOpenModal(campaign)} className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50" title="Edit Campaign"><Edit3 size={16} /></button>
                      
                      <button onClick={() => toggleCampaignStatus(campaign)} className={`p-1 rounded ${campaign.status === '0' ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' : 'text-green-600 hover:text-green-900 hover:bg-green-50'}`} title={campaign.status === '0' ? 'Pause Campaign' : 'Activate Campaign'}>{campaign.status === '1' ? <Pause size={16} /> : <Play size={16} />} </button> 
                     
                      <button onClick={() => deleteCampaign(campaign.id)} className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50" title="Delete Campaign"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {campaigns.length === 0 && !tableRefreshing && (
            <div className="text-center py-12">
              <Megaphone size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {searchQuery.trim()
                  ? 'No campaigns match your search.'
                  : 'No campaigns found. Create your first campaign!'}
              </p>
            </div>
          )}
        </div>

        {!searchQuery.trim() && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            itemLabel="campaigns"
            disabled={tableRefreshing}
            ariaLabel="Campaigns pagination"
          />
        )}
      </div>

      {showCreateModal && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4',
            'bg-white/50 backdrop-blur-md transition-opacity duration-250 ease-out',
            modalAnimatedIn ? 'opacity-100' : 'opacity-0',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="campaign-modal-title"
        >
          <div
            className={cn(
              'w-full max-w-md rounded-xl bg-white p-8 shadow-2xl transition-all duration-250 ease-out',
              modalAnimatedIn ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-2',
            )}
          >
            <h2 id="campaign-modal-title" className="text-xl font-bold text-gray-900 mb-4">
              {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>

            {/* Form Progress Indicator */}
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
                <span>Form Completion</span>
                <span>
                  {Math.round(
                    ([
                      formData.name.trim().length >= 2,
                      formData.advertiser_id,
                      formData.source.trim(),
                      formData.description.trim().length >= 10,
                    ].filter(Boolean).length /
                      4) *
                      100,
                  )}
                  %
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-blue-600 transition-all duration-300"
                  style={{
                    width: `${([
                      formData.name.trim().length >= 2,
                      formData.advertiser_id,
                      formData.source.trim(),
                      formData.description.trim().length >= 10,
                    ].filter(Boolean).length / 4) * 100}%`,
                  }}
                />
              </div>
            </div>

            {modalError && (
              <div
                className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {modalError}
              </div>
            )}
            
             {/* Form Errors */}
             {formErrors.length > 0 && (
               <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                 <div className="flex items-center mb-2">
                   <div className="flex-shrink-0">
                     <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                       <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                     </svg>
                   </div>
                   <div className="ml-3">
                     <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                   </div>
                 </div>
                 <ul className="text-sm text-red-600 space-y-1">
                   {formErrors.map((error, index) => (
                     <li key={index} className="flex items-start">
                       <span className="text-red-500 mr-2">•</span>
                       <span>{error}</span>
                     </li>
                   ))}
                 </ul>
               </div>
             )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Campaign Name <span className="text-red-500">*</span>
                  {formErrors.some(error => error.includes('Campaign name')) && (
                    <span className="text-red-500 ml-1">•</span>
                  )}
                </label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    // Clear related errors when user starts typing
                    if (formErrors.some(error => error.includes('name') || error.includes('Campaign name'))) {
                      setFormErrors(prev => prev.filter(error => !error.includes('name') && !error.includes('Campaign name')));
                    }
                  }}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.some(error => error.includes('name') || error.includes('Campaign name'))
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300',
                  )}
                  placeholder="Enter campaign name (2-100 characters)" 
                  required 
                />
                <div className="flex justify-between items-center mt-1">
                  <div>
                    {(formErrors.some(error => error.includes('Campaign name')) || formErrors.some(error => error.includes('name:'))) && (
                      <p className="text-sm text-red-600">
                        {formErrors.find(error => error.includes('Campaign name')) || 
                         formErrors.find(error => error.includes('name:'))}
                      </p>
                    )}
                  </div>
                  <div className={`text-xs ${
                    formData.name.length < 2 && formData.name.length > 0
                      ? 'text-red-500' 
                      : formData.name.length > 100 
                        ? 'text-red-500' 
                        : 'text-gray-500'
                  }`}>
                    {formData.name.length}/100 characters
                    {formData.name.length < 2 && formData.name.length > 0 && (
                      <span className="ml-1">(minimum 2 required)</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Advertiser <span className="text-red-500">*</span>
                  {formErrors.some(error => error.includes('advertiser')) && (
                    <span className="ml-1 text-red-500">•</span>
                  )}
                </label>
                <select
                  value={formData.advertiser_id} 
                  onChange={(e) => {
                    setFormData({ ...formData, advertiser_id: e.target.value });
                    // Clear related errors when user selects
                    if (formErrors.some(error => error.includes('advertiser') || error.includes('advertiser_id'))) {
                      setFormErrors(prev => prev.filter(error => !error.includes('advertiser') && !error.includes('advertiser_id')));
                    }
                  }}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.some(error => error.includes('advertiser') || error.includes('advertiser_id'))
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300',
                  )}
                  required
                >
                  <option value="">Select Advertiser</option>
                  {advertisers.map(advertiser => (
                    <option key={advertiser.id} value={advertiser.id}>
                      {advertiser.name}
                    </option>
                  ))}
                </select>
                {(formErrors.some(error => error.includes('advertiser')) || formErrors.some(error => error.includes('advertiser_id:'))) && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.find(error => error.includes('advertiser')) || 
                     formErrors.find(error => error.includes('advertiser_id:'))}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Source <span className="text-red-500">*</span>
                  {formErrors.some(error => error.includes('Source')) && (
                    <span className="ml-1 text-red-500">•</span>
                  )}
                </label>
                <select
                  value={formData.source} 
                  onChange={(e) => {
                    setFormData({ ...formData, source: e.target.value });
                    // Clear related errors when user selects
                    if (formErrors.some(error => error.includes('Source'))) {
                      setFormErrors(prev => prev.filter(error => !error.includes('Source')));
                    }
                  }}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.some(error => error.includes('Source'))
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300',
                  )}
                  required
                  disabled={sources.length === 0}
                >
                  <option value="">
                    {sources.length === 0 ? 'Loading sources...' : 'Select Source'}
                  </option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
                {formErrors.some(error => error.includes('Source')) && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.find(error => error.includes('Source'))}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={formData.description} 
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    // Clear related errors when user starts typing
                    if (formErrors.some(error => error.includes('Description'))) {
                      setFormErrors(prev => prev.filter(error => !error.includes('Description')));
                    }
                  }}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter campaign description (optional)"
                />
                <div className="mt-1 flex justify-end">
                  <div className="text-xs text-gray-500">
                    {formData.description.length}/500 characters
                  </div>
                </div>
              </div>
              
              {/* Bottom Error Message */}
              {formErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-600 font-medium">
                      Please fix all validation errors before submitting
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-md bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    formErrors.length > 0 ||
                    !formData.name.trim() ||
                    !formData.advertiser_id ||
                    !formData.source.trim() ||
                    sources.length === 0
                  }
                  className={cn(
                    'rounded-md px-5 py-2.5 text-sm font-medium transition-all',
                    formErrors.length > 0 ||
                      !formData.name.trim() ||
                      !formData.advertiser_id ||
                      !formData.source.trim() ||
                      sources.length === 0
                      ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                      : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  )}
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                  {formErrors.length > 0 && (
                    <span className="ml-2 text-xs">
                      ({formErrors.length} error{formErrors.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;